import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { Search, MapPin, Calendar, Users, ChevronRight, Globe, Anchor, Plane } from 'lucide-react';

interface PortalDemoProps {
  prospect: Prospect;
}

export const PortalDemo: React.FC<PortalDemoProps> = ({ prospect }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    // Apply dynamic theme for the portal
    if (prospect.theme) {
      const root = document.documentElement;
      root.style.setProperty('--ym-prospect-primary', prospect.theme.primary_color);
      if (prospect.theme.bg_color) root.style.setProperty('--ym-prospect-bg', prospect.theme.bg_color);
      if (prospect.theme.text_color) root.style.setProperty('--ym-prospect-text', prospect.theme.text_color);
      if (prospect.theme.surface_color) root.style.setProperty('--ym-prospect-surface', prospect.theme.surface_color);
    }
  }, [prospect]);

  const handleWhatsAppContact = (msg?: string) => {
    const defaultMsg = `Olá, gostaria de solicitar um orçamento de viagem com a ${prospect.trade_name}!`;
    const text = encodeURIComponent(msg || defaultMsg);
    const phone = prospect.whatsapp || '554988416296';
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[var(--ym-prospect-bg)] text-[var(--ym-prospect-text)] font-sans">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[var(--ym-prospect-bg)]/80 backdrop-blur-md border-b border-[var(--ym-prospect-surface)]/20">
        <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-[var(--ym-prospect-primary)] flex items-center justify-center">
              <span className="font-bold text-white text-xl">{prospect.trade_name.charAt(0)}</span>
            </div>
            <span className="font-bold text-xl tracking-tight hidden sm:block">{prospect.trade_name}</span>
          </div>
          <nav className="hidden md:flex gap-8 font-medium text-sm">
            <a href="#" className="hover:text-[var(--ym-prospect-primary)] transition-colors">Destinos</a>
            <a href="#" className="hover:text-[var(--ym-prospect-primary)] transition-colors">Grupos Exclusivos</a>
            <a href="#" className="hover:text-[var(--ym-prospect-primary)] transition-colors">Concierge</a>
            <a href="#" className="hover:text-[var(--ym-prospect-primary)] transition-colors">Minhas Viagens</a>
          </nav>
          <button 
            onClick={() => handleWhatsAppContact()}
            className="px-6 py-2.5 rounded-full bg-[var(--ym-prospect-primary)] text-white font-medium text-sm hover:opacity-90 transition-opacity"
          >
            Solicitar Orçamento
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Abstract Background Placeholder - Normally an image */}
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--ym-prospect-bg)] to-[var(--ym-prospect-surface)]">
           <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay" />
        </div>
        
        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 font-heading tracking-tight">
            Descubra o mundo <br />com <span className="text-[var(--ym-prospect-primary)]">propósito.</span>
          </h1>
          <p className="text-lg md:text-xl opacity-80 mb-12 max-w-2xl mx-auto">
            {prospect.tagline || 'Especialistas em roteiros personalizados, grupos e experiências memoráveis.'}
          </p>

          {/* Search Bar Widget (Wireframe) */}
          <div className="bg-[var(--ym-prospect-surface)] p-2 md:p-4 rounded-2xl shadow-2xl flex flex-col md:flex-row gap-4 max-w-4xl mx-auto border border-white/5">
            <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-[var(--ym-prospect-bg)] rounded-xl border border-white/5">
              <MapPin className="w-5 h-5 opacity-50" />
              <div className="flex-1 text-left">
                <div className="text-xs font-semibold opacity-50 uppercase tracking-wider">Para onde?</div>
                <input type="text" placeholder="Qualquer destino" className="w-full bg-transparent outline-none font-medium mt-1" />
              </div>
            </div>
            
            <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-[var(--ym-prospect-bg)] rounded-xl border border-white/5">
              <Calendar className="w-5 h-5 opacity-50" />
              <div className="flex-1 text-left">
                <div className="text-xs font-semibold opacity-50 uppercase tracking-wider">Quando?</div>
                <input type="text" placeholder="Adicionar datas" className="w-full bg-transparent outline-none font-medium mt-1" />
              </div>
            </div>

            <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-[var(--ym-prospect-bg)] rounded-xl border border-white/5">
              <Users className="w-5 h-5 opacity-50" />
              <div className="flex-1 text-left">
                <div className="text-xs font-semibold opacity-50 uppercase tracking-wider">Quem?</div>
                <input type="text" placeholder="2 passageiros" className="w-full bg-transparent outline-none font-medium mt-1" />
              </div>
            </div>

            <button 
              onClick={() => handleWhatsAppContact('Olá! Fiz uma busca no portal e gostaria de consultar a disponibilidade de pacotes de viagem.')}
              className="bg-[var(--ym-prospect-primary)] hover:opacity-90 text-white rounded-xl px-8 py-4 flex items-center justify-center gap-2 font-bold transition-all"
            >
              <Search className="w-5 h-5" />
              Buscar
            </button>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl font-bold font-heading">O que você procura?</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Globe, label: 'Internacional' },
              { icon: Plane, label: 'Nacional' },
              { icon: Users, label: 'Grupos Acompanhados' },
              { icon: Anchor, label: 'Cruzeiros' }
            ].map((cat, idx) => (
              <div key={idx} className="group cursor-pointer bg-[var(--ym-prospect-surface)] border border-white/5 p-8 rounded-2xl flex flex-col items-center justify-center text-center hover:border-[var(--ym-prospect-primary)] transition-colors">
                <cat.icon className="w-10 h-10 mb-4 opacity-70 group-hover:text-[var(--ym-prospect-primary)] transition-colors" />
                <h3 className="font-semibold">{cat.label}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Packages (Wireframe) */}
      <section className="py-20 px-4 bg-[var(--ym-prospect-surface)]/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold font-heading mb-2">Roteiros em Destaque</h2>
              <p className="opacity-70">Experiências selecionadas pela nossa equipe.</p>
            </div>
            <button className="flex items-center gap-2 font-semibold text-[var(--ym-prospect-primary)] hover:underline">
              Ver todos <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Package Card 1 */}
            <div className="group rounded-2xl overflow-hidden bg-[var(--ym-prospect-surface)] border border-white/5 cursor-pointer">
              <div className="h-64 bg-slate-800 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&q=80')] bg-cover bg-center group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                  <div>
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold text-white mb-2 inline-block">GRUPO EXCLUSIVO</span>
                    <h3 className="text-xl font-bold text-white">Paris & Roma 2027</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/80">A partir de</div>
                    <div className="font-bold text-white">Consulte</div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm opacity-70 mb-4 line-clamp-2">Uma experiência imersiva com guia acompanhante pelas capitais mais charmosas da Europa.</p>
                <div className="flex gap-4 text-sm font-medium">
                  <div className="flex items-center gap-1 opacity-80"><Calendar className="w-4 h-4" /> 12 dias</div>
                  <div className="flex items-center gap-1 opacity-80"><Plane className="w-4 h-4" /> Voo incluso</div>
                </div>
              </div>
            </div>
            
             {/* Package Card 2 */}
             <div className="group rounded-2xl overflow-hidden bg-[var(--ym-prospect-surface)] border border-white/5 cursor-pointer">
              <div className="h-64 bg-slate-800 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1599839619722-39751411ea63?auto=format&fit=crop&q=80')] bg-cover bg-center group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                  <div>
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold text-white mb-2 inline-block">RESORT</span>
                    <h3 className="text-xl font-bold text-white">Férias em Cancún</h3>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm opacity-70 mb-4 line-clamp-2">All-inclusive premium para a família inteira com as melhores praias do Caribe.</p>
                <div className="flex gap-4 text-sm font-medium">
                  <div className="flex items-center gap-1 opacity-80"><Calendar className="w-4 h-4" /> 7 dias</div>
                  <div className="flex items-center gap-1 opacity-80"><Anchor className="w-4 h-4" /> All-inclusive</div>
                </div>
              </div>
            </div>

             {/* Package Card 3 */}
             <div className="group rounded-2xl overflow-hidden bg-[var(--ym-prospect-surface)] border border-white/5 cursor-pointer">
              <div className="h-64 bg-slate-800 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1587595431973-160d0d94add1?auto=format&fit=crop&q=80')] bg-cover bg-center group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                  <div>
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold text-white mb-2 inline-block">SÓ PARA ELAS</span>
                    <h3 className="text-xl font-bold text-white">Expedição Peru</h3>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm opacity-70 mb-4 line-clamp-2">Grupo feminino acompanhado para Machu Picchu e Valle Sagrado com experiências culturais únicas.</p>
                <div className="flex gap-4 text-sm font-medium">
                  <div className="flex items-center gap-1 opacity-80"><Calendar className="w-4 h-4" /> 9 dias</div>
                  <div className="flex items-center gap-1 opacity-80"><Users className="w-4 h-4" /> Vagas limitadas</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informational Footer */}
      <footer className="py-12 px-4 border-t border-[var(--ym-prospect-surface)] text-center opacity-60 text-sm">
        <p>Demonstração criada pela YouMachine para {prospect.name}</p>
        <p className="mt-2">Isto é um wireframe conceitual. Pacotes e valores são apenas ilustrativos.</p>
      </footer>
    </div>
  );
};
